import { css } from '@emotion/css';
import { Col, Row } from 'antd';
import { useAccountSelector } from 'src/hooks';
import {
  ClientServicesLink,
  MTooltip,
  MTypography,
  RetirementAccountContributionFormLink,
  RetirementAccountDistributionFormLink,
} from 'src/lib';
import { Color, Spacing } from 'src/styles';
import { isRetirementAccountFundingAllowed } from 'src/utils';

import { DepositByCheckInstructions } from './components/DepositByCheckInstructions/DepositByCheckInstructions';
import { DepositByWireInstructions } from './components/DepositByWireInstructions/DepositByWireInstructions';

const fundsHeadingSpacing = css`
  && {
    ${Spacing.mt16}
    ${Spacing.mb8}
  }
`;

/** Beats MTypography's margin reset, and collapses like a plain paragraph. */
const paragraph = css`
  && {
    ${Spacing.my16}
  }
`;

/** Space above the IRA contribution check and wire instructions titles. */
const instructionsHeading = css`
  && {
    ${Spacing.mt16}
  }
`;

export const RetirementAccountFunding = () => {
  const { account } = useAccountSelector();

  const renderIraContributionInstructions = (type: 'check' | 'wire') => {
    return (
      <>
        <MTypography variant='body1' className={paragraph}>
          I would like to make a:
        </MTypography>
        <ul className={Spacing.pl20}>
          <li>
            <MTypography variant='body1'>
              <MTypography variant='body1' weight='bold' inline>
                IRA Contribution
              </MTypography>
              {': complete, sign and return with your payment type.'}
              <br />
              <RetirementAccountContributionFormLink />
              <br />
              Funds being contributed into or distributed from retirement accounts may entail tax consequences.
              <br />
              Contributions are limited and withdrawals before age 59 1/2 may be subject to a penalty tax.
            </MTypography>
          </li>
        </ul>
        <MTypography variant='body1' className={paragraph}>
          All IRA contributions count toward your annual limit.
          <br />
          If you made contributions to another external IRA, you&apos;ll want to factor in those amounts when
          contributing to your My IPO IRA. contribution limits can change annually. Visit the{' '}
          <a
            href='https://www.irs.gov/retirement-plans/retirement-plans-faqs-regarding-iras-distributions-withdrawals'
            target='_blank'
            rel='noreferrer'>
            IRS website
          </a>{' '}
          and consult a tax advisor for your specific situation.
        </MTypography>
        {type === 'wire' ? (
          <>
            <MTypography variant='body1' weight='bold' className={instructionsHeading}>
              WIRE INSTRUCTIONS
            </MTypography>
            <DepositByWireInstructions />
          </>
        ) : (
          <>
            <MTypography variant='body1' weight='bold' className={instructionsHeading}>
              CHECK INSTRUCTIONS
            </MTypography>
            <DepositByCheckInstructions />
          </>
        )}
      </>
    );
  };

  const renderTransferFundsFromAnotherIraAccount = () => {
    return (
      <>
        <MTypography variant='body1' className={paragraph}>
          I would like to:
        </MTypography>
        <ul className={Spacing.pl20}>
          <li>
            <MTypography variant='body1'>
              <MTypography variant='body1' weight='bold' inline>
                Transfer funds from another IRA Account
              </MTypography>
              {
                ': complete, sign and return with a copy of your most recent IRA statement from the financial firm you would like to transfer funds.'
              }
              <br />
              <a
                href='https://legacy.tradingblock.com/Admin/Forms/Apex/Account_Transfer/ACAT_Transfer.pdf'
                target='_blank'
                rel='noreferrer'>
                ACAT Form
              </a>
              <br /> A transfer is a non-reportable movement of funds between two retirement accounts of the same type,
              such as transferring money from one Traditional IRA into another Traditional IRA. This type of transfer
              doesn&apos;t generate a tax form and isn&apos;t reported to the IRS.
            </MTypography>
          </li>
          <li>
            <MTypography variant='body1'>
              <MTypography variant='body1' weight='bold' inline>
                Example:
              </MTypography>{' '}
              If you transfer a Traditional IRA with XYZ Financial to your Traditional IRA at My IPO.
            </MTypography>
          </li>
        </ul>
      </>
    );
  };

  const renderDirectTransferFromIdenticalIraAccount = () => {
    return (
      <>
        <MTypography variant='body1' className={paragraph}>
          I would like to make a:
        </MTypography>
        <ul className={Spacing.pl20}>
          <li>
            <MTypography variant='body1'>
              <MTypography variant='body1' weight='bold' inline>
                Direct Transfer
              </MTypography>
              {': complete, sign and return with your payment type.'}
              <br />
              <RetirementAccountContributionFormLink label='Direct Transfer Form' />
            </MTypography>
          </li>
        </ul>
        <MTypography variant='body1' weight='bold'>
          WIRE INSTRUCTIONS
        </MTypography>
        <DepositByWireInstructions />
      </>
    );
  };

  const renderIraWithdrawalInstructions = () => {
    return (
      <>
        <MTypography variant='body1' className={paragraph}>
          I would like to make a:
        </MTypography>
        <ul className={Spacing.pl20}>
          <li>
            <MTypography variant='body1'>
              <MTypography variant='body1' weight='bold' inline>
                IRA Withdrawal
              </MTypography>
              {': complete, sign and return with your payment type.'}
              <br />
              <RetirementAccountDistributionFormLink />
              <br />
              Funds being contributed into or distributed from retirement accounts may entail tax consequences.
              Contributions are limited and withdrawals before age 59 1/2 may be subject to a penalty tax.
            </MTypography>
          </li>
        </ul>
        <MTypography variant='body1' className={paragraph}>
          Contents provided are for informational purposes only and don&apos;t constitute tax or investment advice. For
          specific questions, you should consult a tax professional. My IPO does not provide tax advice. Links to third
          party sites are being provided for informational purposes only.
        </MTypography>
      </>
    );
  };

  return (
    <Row>
      <Col span={24}>
        <MTypography variant='subtitle1' color={Color.Gray8} testId='retirement-bank-account-disclaimer'>
          Please select one of the following to find more information regarding depositing or withdrawing funds from
          your account, then download appropriate form and submit with required information below to{' '}
          <ClientServicesLink />.
        </MTypography>
      </Col>
      <Col span={24}>
        <MTypography
          variant='h5'
          weight='semibold'
          color={Color.Gray8}
          className={fundsHeadingSpacing}
          testId='deposit-funds'>
          DEPOSIT FUNDS
        </MTypography>
        <Row gutter={[0, 4]}>
          {isRetirementAccountFundingAllowed(account?.retirementAccountType?.value) && (
            <Col span={24}>
              <MTooltip
                type='modal'
                label='IRA Contribution Wire'
                title='IRA Contribution Wire'
                icon='info'
                testId={'ira-contribution-wire'}>
                {renderIraContributionInstructions('wire')}
              </MTooltip>
            </Col>
          )}
          {isRetirementAccountFundingAllowed(account?.retirementAccountType?.value) && (
            <Col span={24}>
              <MTooltip
                type='modal'
                label='IRA Contribution Check'
                title='IRA Contribution Check'
                icon='info'
                testId={'ira-contribution-check'}>
                {renderIraContributionInstructions('check')}
              </MTooltip>
            </Col>
          )}
          <Col span={24}>
            <MTooltip
              type='modal'
              label='Transfer funds from another IRA Account'
              title='Transfer funds from another IRA Account'
              icon='info'
              testId={'transfer-funds-from-another-ira'}>
              {renderTransferFundsFromAnotherIraAccount()}
            </MTooltip>
          </Col>
          <Col span={24}>
            <MTooltip
              type='modal'
              label='Direct Transfer from Identical IRA/IRA Rollover Wire'
              title='Direct Transfer from Identical IRA/IRA Rollover Wire'
              icon='info'
              testId={'direct-transfer-from-identical-ira-rollover'}>
              {renderDirectTransferFromIdenticalIraAccount()}
            </MTooltip>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <MTypography
          variant='h5'
          weight='semibold'
          color={Color.Gray8}
          className={fundsHeadingSpacing}
          testId='withdrawal-funds'>
          WITHDRAWAL FUNDS
        </MTypography>
        <Row>
          <Col span={24}>
            <MTooltip
              type='modal'
              label='IRA Withdrawal (Check/Wire)'
              title='IRA Withdrawal (Check/Wire)'
              icon='info'
              testId={'ira-withdrawal-check-wire'}>
              {renderIraWithdrawalInstructions()}
            </MTooltip>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
